import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Users, PhoneCall, CalendarOff, ClipboardList, Cake, ArrowRight, Timer, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, EVENT_LABELS, initials, photoUrl } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Failed to load dashboard"));
  }, []);

  if (!data) return <div className="py-20 text-center text-sm text-slate-400" data-testid="dashboard-loading">Loading…</div>;

  const widgets = [
    { label: "On Duty Today", value: data.on_duty.length, icon: Users, testid: "widget-on-duty" },
    { label: "On Call", value: data.on_call.length, icon: PhoneCall, testid: "widget-on-call" },
    { label: "Time Off / Call-Offs", value: data.time_off.length, icon: CalendarOff, testid: "widget-time-off" },
    { label: "Visits Today", value: data.visits.length, icon: ClipboardList, testid: "widget-visits" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
          {format(new Date(data.date + "T12:00:00"), "EEEE, MMMM d, yyyy")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">Today's staffing overview for your hospice team.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {widgets.map(({ label, value, icon: Icon, testid }) => (
          <Card key={label} className="rounded-lg border-slate-200 shadow-sm" data-testid={testid}>
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100">
                <Icon className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="font-heading text-2xl font-semibold leading-none text-slate-900">{value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.15em] text-slate-500">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-lg border-slate-200" data-testid="on-duty-list">
          <CardHeader className="flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-lg font-medium">On Duty (8a – 5p)</CardTitle>
            <Link to="/schedule" className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="view-schedule-link">
              Schedule <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.on_duty.length === 0 && <p className="text-sm text-slate-400">No one scheduled today.</p>}
            {data.on_duty.map((e) => (
              <Link key={e.id} to={`/employees/${e.id}`} className="flex items-center gap-3 rounded-md border border-slate-100 p-2.5 transition-colors hover:border-slate-300" data-testid={`on-duty-${e.id}`}>
                <Avatar className="h-8 w-8"><AvatarImage src={photoUrl(e)} alt={e.name} /><AvatarFallback className="bg-blue-50 text-xs font-semibold text-blue-700">{initials(e.name)}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{e.name}</p>
                  <p className="text-xs text-slate-500">{e.position}</p>
                </div>
              </Link>
            ))}
            {data.on_call.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-md border border-purple-100 bg-purple-50/50 p-2.5" data-testid={`on-call-${e.id}`}>
                <Avatar className="h-8 w-8"><AvatarImage src={photoUrl(e)} alt={e.name} /><AvatarFallback className="bg-purple-100 text-xs font-semibold text-purple-700">{initials(e.name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{e.name}</p>
                  <p className="text-xs text-slate-500">{e.position}</p>
                </div>
                <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">On Call</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="rounded-lg border-slate-200" data-testid="timeoff-list">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-lg font-medium">Time Off & Attendance Today</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {data.time_off.length === 0 && <p className="text-sm text-slate-400">No time-off events today.</p>}
              {data.time_off.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between rounded-md border border-slate-100 p-2.5" data-testid={`timeoff-${ev.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{ev.employee_name}</p>
                    {ev.notes && <p className="text-xs text-slate-500">{ev.notes}</p>}
                  </div>
                  <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">{EVENT_LABELS[ev.type]}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="rounded-lg border-slate-200" data-testid="overtime-watch">
            <CardHeader className="pb-3"><CardTitle className="font-heading flex items-center gap-2 text-lg font-medium"><Timer className="h-4 w-4" /> Overtime Watch — This Week</CardTitle></CardHeader>
            <CardContent className="space-y-2.5">
              {(!data.overtime_week || data.overtime_week.length === 0) && <p className="text-sm text-slate-400">No overtime this week.</p>}
              {(data.overtime_week || []).map((t) => (
                <div key={t.employee_id} className="flex items-center gap-3" data-testid={`ot-${t.employee_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{t.name}</p>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className={`h-full rounded-full ${t.total_hours >= 10 ? "bg-[#F43F5E]" : "bg-[#2C5545]"}`} style={{ width: `${Math.min(100, (t.total_hours / 20) * 100)}%` }} />
                    </div>
                  </div>
                  <span className={`flex items-center gap-1 text-xs font-bold ${t.total_hours >= 10 ? "text-[#9F1239]" : "text-[#2C5545]"}`}>
                    {t.total_hours >= 10 && <AlertTriangle className="h-3.5 w-3.5" />}{t.total_hours}h OT
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="rounded-lg border-slate-200" data-testid="visits-list">
            <CardHeader className="flex-row items-center justify-between pb-3">
              <CardTitle className="font-heading text-lg font-medium">Patient Visits Today</CardTitle>
              <Link to="/patients" className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="view-visits-link">
                Manage <ArrowRight className="h-3 w-3" />
              </Link>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.visits.length === 0 && <p className="text-sm text-slate-400">No visits scheduled today.</p>}
              {data.visits.map((v) => (
                <div key={v.id} className="flex items-center justify-between rounded-md border border-slate-100 p-2.5" data-testid={`visit-${v.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{v.patient_name}</p>
                    <p className="text-xs text-slate-500">{v.employee_name}{v.purpose ? ` · ${v.purpose}` : ""}</p>
                  </div>
                  <span className="text-xs font-medium text-slate-600">{v.time || "—"}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {data.birthdays.length > 0 && (
            <Card className="rounded-lg border-slate-200" data-testid="birthdays-list">
              <CardHeader className="pb-3"><CardTitle className="font-heading flex items-center gap-2 text-lg font-medium"><Cake className="h-4 w-4" /> Upcoming Birthdays</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {data.birthdays.map((b) => (
                  <div key={b.id} className="flex items-center justify-between text-sm" data-testid={`birthday-${b.id}`}>
                    <span className="font-medium text-slate-900">{b.name}</span>
                    <span className="text-xs text-slate-500">
                      {format(new Date(b.date + "T12:00:00"), "MMM d")}{b.days === 0 ? " — today!" : ` (in ${b.days}d)`}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
